// Buyer-Side Diagnostic view controller (mounts into #buyer-app). A standalone diagnostic: it reads
// what the BUYER revealed and predicts deal momentum for a CRO. Three states — pre-run (reuse the
// already-imported transcript, or import another), running (high-level phase strip), results
// (text-left / visuals-right). Reuses the existing import + streaming-run plumbing.
//
// All model-derived text is written via textContent (never innerHTML) — the app's CSP/no-raw-HTML
// posture. Meter widths and the evidence-dial arc are set through the CSSOM (element.style.*), which
// CSP allows; inline style="" attributes in markup do not.

import type { BuyerSideResult, BuyerBand, RunEvent, WhySignals } from '../lib/types.js';
import { runBuyerSide } from '../lib/api.js';
import { renderBuyerRadar, renderBuyerRadarDemo } from './buyerRadar.js';
import { navigateTo } from './sidenav.js';
import { wireCollapse } from '../lib/collapse.js';

// Buyer Signals is part of the One-Off Diagnostic group, which is included on EVERY plan (no feature
// gating). It auto-runs whenever a call is scored and is always in the PDF report — there is no
// subscription gate. (Pricing decision: all generally-available features on all tiers.)

const PHASES: Array<[string, string]> = [
  ['isolating', "Isolating the buyer's voice"],
  ['intent', 'Reading belief'],
  ['commitment', 'Reading commitment'],
  ['evidence', 'Scoring the evidence'],
  ['momentum', 'Plotting momentum'],
];

// The buyer run can complete in a blink; the Runtime Monitor transitions are paced client-side so the
// eye can actually follow each stage lighting up (and the results wait for the animation to finish).
const PHASE_STEP_MS = 520;
const delay = (ms: number): Promise<void> => new Promise((r) => setTimeout(r, ms));

const BAND_CLASS: Record<BuyerBand, string> = { None: 'b-none', Emerging: 'b-emerging', Established: 'b-established', Strong: 'b-strong' };

// The band pill shows a plain-language rung of the axis's own scale rather than the internal band
// word — so Belief reads as a conviction ladder and Commitment as a "window shopper → take my money"
// buying ladder. The underlying BuyerBand still drives colour (BAND_CLASS) and every other consumer.
const BAND_WORDS: Record<'belief' | 'commitment' | 'evidence', Record<BuyerBand, string>> = {
  belief: { None: 'Blocker', Emerging: 'Skeptic', Established: 'Believer', Strong: 'Potential Mobilizer' },
  commitment: { None: 'Window Shopper', Emerging: 'Kicking Tires', Established: 'Reaching for Wallet', Strong: 'Take My Money' },
  evidence: { None: 'Thin', Emerging: 'Limited', Established: 'Solid', Strong: 'Strong' },
};

// One-line "how to read this" copy per bar — shown on hover as a tooltip, so it stays off-screen
// until wanted (Category 3: tooltips replace on-screen sub-text).
const METER_TIP: Record<'belief' | 'commitment' | 'evidence', string> = {
  belief: 'How strongly the buyer believes this solves their problem, in their own words. Scale: Blocker to Potential Mobilizer.',
  commitment: 'How far the buyer is actually moving the deal: owned next steps, stakeholders, timing. Scale: Window Shopper to Take My Money.',
  evidence: 'How much to trust this read: based on how much the buyer actually spoke, grounded in the transcript and attributed to them, never the seller.',
};

interface Source { text: string; name?: string }

/** Small DOM helper — HTML element with class + optional plain text (text is set safely). */
function h<K extends keyof HTMLElementTagNameMap>(tag: K, cls?: string, text?: string): HTMLElementTagNameMap[K] {
  const n = document.createElement(tag);
  if (cls) n.className = cls;
  if (text !== undefined) n.textContent = text;
  return n;
}

export class BuyerSidePage {
  private root: HTMLElement | null;
  private getShared: () => Source | null;
  private onImport: () => void;
  private onResult: () => void;
  private onRunStateChange: () => void;
  private abort: AbortController | null = null;
  private phaseNodes = new Map<string, HTMLElement>();
  /** Paced queue for the Runtime Monitor transitions (see PHASE_STEP_MS). */
  private phaseQueue: Array<() => void> = [];
  private phasePumping = false;
  private phasesCanceled = false;
  /** The last completed analysis, exposed for the diagnostic snapshot so a refresh restores it. */
  private lastResult: BuyerSideResult | null = null;
  /** True while a file is being imported (classify in flight) — swaps the idle CTA for a live
   *  "please wait" heartbeat so the user gets feedback on this lens too, not just Quick Snapshot. */
  private importing = false;

  /** getShared = the shared current call (from main.ts); onImport = open the shared import picker;
   *  onResult = notify the host (to persist) when an analysis completes; onRunStateChange = fired when
   *  the buyer run starts/ends so the host can refresh the call-bar (the Stop action lives there). */
  constructor(getShared: () => Source | null, onImport: () => void, onResult: () => void = () => {}, onRunStateChange: () => void = () => {}) {
    this.getShared = getShared;
    this.onImport = onImport;
    this.onResult = onResult;
    this.onRunStateChange = onRunStateChange;
    this.root = document.getElementById('buyer-app');
    if (this.root) this.renderIdle();
  }

  /** The last completed Buyer-Signals result (or null). Read by main.ts when persisting the snapshot. */
  getResult(): BuyerSideResult | null {
    return this.lastResult;
  }

  /** True while an analysis is actually running (drives the call-bar Stop action). */
  isRunning(): boolean {
    return this.abort !== null;
  }

  /** Abort the in-flight analysis — called by the call-bar's Stop action. */
  stop(): void {
    this.abort?.abort();
  }

  /** Re-render a persisted result on startup (restore) — no re-fetch, no persist callback. */
  restore(result: BuyerSideResult): void {
    if (!this.root) return;
    this.lastResult = result;
    this.renderResults(result);
  }

  /** Re-render for the current shared call when the view is shown. Preserves a run or shown results. */
  onShow(): void {
    if (!this.root || this.abort) return;
    if (!this.root.querySelector('.bs-results')) this.renderIdle();
  }

  /** Kept for the host's import lifecycle calls. Import progress now shows in the shared Message
   *  Center (Diagnostic Controls strip), so the idle state itself is static; nothing to re-render. */
  setImporting(v: boolean): void {
    this.importing = v;
  }

  /** Force back to idle — called when the shared call CHANGES (import/clear). Aborts any in-flight
   *  auto-run first, so a run started for the OLD call can't finish and render stale results. */
  reset(): void {
    if (!this.root) return;
    if (this.abort) {
      this.abort.abort();
      this.abort = null;
    }
    this.phasesCanceled = true; // drop any queued Runtime Monitor transitions
    this.phaseQueue = [];
    this.renderIdle();
  }

  /** Auto-run when the shared call is scored on Quick Snapshot (main.ts calls this after a Run).
   *  Runs silently in the background so results await the user on this lens. Self-guards: skips if a
   *  run is in flight or results already exist for this call. */
  autoRun(): void {
    if (!this.root || this.abort) return;
    if (this.root.querySelector('.bs-results')) return; // already analyzed this call
    if (!this.source()) return;
    void this.start();
  }

  private source(): Source | null {
    const s = this.getShared();
    return s && s.text.trim() ? s : null;
  }

  private renderIdle(): void {
    this.lastResult = null; // no result on screen anymore
    const root = this.root!;
    root.textContent = '';

    // Two columns, no frame: a short intro on the left, a live demo radar on the right. Import / Run
    // live in the shared Diagnostic Controls strip above, so there is no CTA here — this is a
    // forecasting tool for CROs, not a training page (the detail lives in Getting Started).
    const grid = h('div', 'bs-idle');
    const left = h('div', 'bs-idle-left');

    // 1. Two-line intro (what it is).
    left.appendChild(
      h(
        'p',
        'bs-lede',
        "Buyer Signals reads the deal from the buyer's side: how much they believe the solution fits and how ready they are to act, so you can forecast and manage deals with more confidence.",
      ),
    );

    // Import + Run live in the shared Diagnostic Controls strip above; this just tells the user where.
    left.appendChild(h('p', 'bs-idle-hint', 'Import a call and hit Run in the Diagnostic Controls above; the buyer read appears here automatically, no need to leave this page.'));

    // Full-walkthrough link, pinned to the bottom of the column (margin-top:auto in CSS).
    const guideP = h('p', 'bs-idle-guide');
    const guide = h('a', 'bs-intro-link', 'Read the full walkthrough in Getting Started');
    (guide as HTMLAnchorElement).href = '#';
    guide.addEventListener('click', (e) => { e.preventDefault(); navigateTo('getting-started'); });
    guideP.appendChild(guide);
    left.appendChild(guideP);
    grid.appendChild(left);

    const right = h('div', 'bs-idle-right');
    const radarWrap = h('div', 'bs-radar-wrap bs-idle-radar');
    renderBuyerRadarDemo(radarWrap);
    right.appendChild(radarWrap);
    grid.appendChild(right);

    root.appendChild(grid);
  }

  // ---- Running ----
  private async start(): Promise<void> {
    const src = this.source();
    if (!src || this.abort) return;
    this.abort = new AbortController();
    this.phasesCanceled = false;
    this.phaseQueue = [];
    this.phasePumping = false; // fresh run: ensure the pump can start even if a prior one is unwinding
    this.onRunStateChange(); // now running → call-bar shows Stop
    this.renderRunning();
    try {
      for await (const ev of runBuyerSide(src.text, this.abort.signal, false)) {
        this.handleEvent(ev);
        if (ev.t === 'buyer_side_ready' || ev.t === 'run_error' || ev.t === 'run_stopped') break;
      }
    } catch (e) {
      if (!this.abort?.signal.aborted) this.renderError(e instanceof Error ? e.message : 'The analysis failed.');
    } finally {
      this.abort = null;
      this.onRunStateChange(); // no longer running → call-bar hides Stop
    }
  }

  private handleEvent(ev: RunEvent): void {
    if (ev.t === 'buyer_phase') {
      const node = this.phaseNodes.get(ev.phase);
      const state = ev.state;
      if (node) this.enqueuePhase(() => { node.className = 'cb-box ' + (state === 'done' ? 'cb-done' : 'cb-running'); });
    } else if (ev.t === 'buyer_side_ready') {
      this.lastResult = ev.result;
      const result = ev.result;
      // Hold the results until the paced Runtime Monitor animation has finished, so the stages are
      // actually visible even when scoring returns in a blink.
      this.enqueuePhase(() => { this.renderResults(result); this.onResult(); });
    } else if (ev.t === 'run_error') {
      this.phasesCanceled = true;
      this.phaseQueue = [];
      this.renderError(ev.message);
    } else if (ev.t === 'run_stopped') {
      this.phasesCanceled = true;
      this.phaseQueue = [];
      this.renderIdle();
    }
  }

  /** Enqueue a Runtime Monitor transition and pump the queue at PHASE_STEP_MS so the eye can follow. */
  private enqueuePhase(fn: () => void): void {
    this.phaseQueue.push(fn);
    void this.pumpPhases();
  }

  private async pumpPhases(): Promise<void> {
    if (this.phasePumping) return;
    this.phasePumping = true;
    while (this.phaseQueue.length) {
      if (this.phasesCanceled) break;
      const fn = this.phaseQueue.shift()!;
      fn();
      await delay(PHASE_STEP_MS);
    }
    this.phasePumping = false;
  }

  private renderRunning(): void {
    this.lastResult = null;
    const root = this.root!;
    root.textContent = '';
    this.phaseNodes.clear();
    // No page-title header — the lens tab already names this view. Progress uses the same Runtime
    // Monitor language as Quick Snapshot (glowing-gold running box, grey when done).
    root.appendChild(h('h2', 'section-h', 'Runtime Monitor'));
    const board = h('div', 'control-board bs-board');
    for (const [id, label] of PHASES) {
      const box = h('div', 'cb-box cb-idle', label);
      board.appendChild(box);
      this.phaseNodes.set(id, box);
    }
    root.appendChild(board);
    // Reassure that navigating away doesn't lose the run (onShow guards while this.abort is set).
    const note = h('p', 'bs-run-note');
    note.appendChild(document.createTextNode('You can switch to '));
    const sk = h('a', 'bs-intro-link', 'Quick Snapshot');
    (sk as HTMLAnchorElement).href = '#';
    sk.addEventListener('click', (e) => { e.preventDefault(); navigateTo('diagnostic'); });
    note.appendChild(sk);
    note.appendChild(document.createTextNode(' and come back; the analysis keeps running here.'));
    root.appendChild(note);
    // Stop now lives in the call-bar action row (left of Download PDF), not in the content.
  }

  private renderError(msg: string): void {
    this.lastResult = null;
    const root = this.root!;
    root.textContent = '';
    root.appendChild(h('h2', 'bs-title', 'Analysis unavailable'));
    root.appendChild(h('p', 'bs-lede', msg));
    const again = h('button', 'btn-primary');
    (again as HTMLButtonElement).type = 'button';
    again.textContent = 'Start over';
    again.addEventListener('click', () => this.renderIdle());
    root.appendChild(again);
  }

  /** Reset to the idle state — invoked by the call-bar's "New analysis" action (it lives up in the
   *  call-bar frame, above the divider, not in this content column). */
  newAnalysis(): void {
    this.renderIdle();
  }

  // ---- Results: one stacked column, every block above the next (no two-column "holes") ----
  private renderResults(r: BuyerSideResult): void {
    const root = this.root!;
    root.textContent = '';

    // Two columns from the top: the quadrant header, headline, gauges + evidence on the LEFT (kept to
    // the left half so it never runs under the radar); the radar on the RIGHT, top-aligned with the
    // header (bs-results align-items:start).
    const results = h('div', 'bs-results');
    results.appendChild(this.metricsCol(r));
    results.appendChild(this.visualCol(r));
    root.appendChild(results);

    // Full-width stack below the gauges + radar row, in order: the buyer's buying case, then the
    // coach actions, then the belief/commitment evidence quotes. Each spans edge to edge.
    const why = this.whySection(r);
    if (why) root.appendChild(why);
    const actions = this.actionsSection(r);
    if (actions) root.appendChild(actions);
    const quotes = this.quotesSection(r);
    if (quotes) root.appendChild(quotes);
  }

  /** Left half of the top row: quadrant header + headline, then the three gauges. (Actions and the
   *  evidence quotes are now full-width sections BELOW the gauges + radar row — see renderResults.) */
  private metricsCol(r: BuyerSideResult): HTMLElement {
    const col = h('div', 'bs-col bs-text');

    // TL;DR at the top of the column — quadrant header then the takeaway sentence.
    const tldr = h('div', 'bs-tldr');
    tldr.appendChild(h('div', `bs-tldr-quad q-${r.quadrant}`, quadLabel(r.quadrant)));
    tldr.appendChild(h('p', 'bs-tldr-tx', r.headline));
    col.appendChild(tldr);

    const bars = h('div', 'bs-bars');
    bars.appendChild(this.meter('Belief', r.intent, r.intent_band, 'belief'));
    bars.appendChild(this.meter('Commitment', r.commitment, r.commitment_band, 'commitment'));
    const attrLine = evidenceAttrLine(r.attribution);
    bars.appendChild(this.meter('Evidence', r.evidence_score, r.evidence_band, 'evidence', attrLine));
    col.appendChild(bars);
    return col;
  }

  /** Full-width "What to do next" — the coach actions. Null when there are none. */
  private actionsSection(r: BuyerSideResult): HTMLElement | null {
    if (!r.actions.length) return null;
    return this.section('What to do next', (card) => {
      const ul = h('ul', 'bs-acts');
      for (const a of r.actions) ul.appendChild(h('li', undefined, a));
      card.appendChild(ul);
    });
  }

  /** Full-width "Belief & Commitment Quotes" — the buyer-attributed evidence. Null when there are none.
   *  Collapsible via its header (default collapsed, persisted); Copy sits beside the header. */
  private quotesSection(r: BuyerSideResult): HTMLElement | null {
    if (!r.quotes.length) return null;
    const ev = h('div', 'bs-section bs-quotes');
    const head = h('div', 'col-head');
    const title = h('h3', 'col-h', 'Belief & Commitment Quotes');
    head.appendChild(title);
    head.appendChild(this.copyButton('the belief & commitment quotes', () => quotesToText(r.quotes)));
    ev.appendChild(head);
    const body = h('div', 'bs-quotes-body');
    for (const q of r.quotes) {
      const item = h('div', 'bs-quote');
      item.appendChild(h('div', 'bs-quote-tx', `“${q.quote}”`));
      item.appendChild(h('div', `bs-quote-tag axis-${q.axis}`, q.label));
      // The model's one-line rationale — why this line is solid evidence (the CRO-confidence part).
      if (q.why) item.appendChild(h('div', 'bs-quote-why', q.why));
      body.appendChild(item);
    }
    ev.appendChild(body);
    // Collapse from the title only, so clicking Copy never toggles the section.
    wireCollapse(title, body, 'bs-quotes');
    return ev;
  }

  /** Right column: the momentum radar. */
  private visualCol(r: BuyerSideResult): HTMLElement {
    const col = h('div', 'bs-col bs-visual');
    const radarWrap = h('div', 'bs-radar-wrap');
    renderBuyerRadar(radarWrap, [{ intent: r.intent, commitment: r.commitment, quadrant: r.quadrant, primary: true }]);
    col.appendChild(radarWrap);
    return col;
  }

  /** The buyer's buying case, in their own words — verbatim quotes for the four qualification
   *  questions, no scoring. Returns null for results persisted before this section existed. A per-
   *  question "no evidence found" note keeps every question visible even when nothing was found. */
  private whySection(r: BuyerSideResult): HTMLElement | null {
    const w = r.why_signals;
    if (!w) return null;

    const wrap = h('div', 'bs-section bs-why');
    const head = h('div', 'col-head');
    head.appendChild(h('h3', 'col-h', 'Why the buyer is buying'));
    head.appendChild(this.copyButton("the buyer's buying case", () => whyToText(w)));
    wrap.appendChild(head);
    wrap.appendChild(
      h('p', 'bs-why-lede', "The buyer's own words behind the four questions that decide the deal. Quotes only, straight from the call, never the seller's."),
    );

    const card = h('div', 'bs-card-pad bs-why-card');
    for (const [key, label] of WHY_QUESTIONS) {
      const block = h('div', 'bs-why-q');
      block.appendChild(h('h4', 'bs-why-qh', label));
      const quotes = w[key];
      if (quotes.length) {
        const ul = h('ul', 'bs-why-list');
        for (const q of quotes) ul.appendChild(h('li', 'bs-why-quote', `“${q}”`));
        block.appendChild(ul);
      } else {
        block.appendChild(h('p', 'bs-why-empty', 'No evidence found in this call.'));
      }
      card.appendChild(block);
    }
    wrap.appendChild(card);
    return wrap;
  }

  /** A "Copy" button matching the app's Score-Analysis copy control. Self-wired (this button is
   *  created after main.ts's one-time .copy-btn wiring, so it carries its own click handler). */
  private copyButton(what: string, getText: () => string): HTMLButtonElement {
    const btn = h('button', 'copy-btn') as HTMLButtonElement;
    btn.type = 'button';
    btn.title = `Copy ${what}`;
    btn.setAttribute('aria-label', `Copy ${what}`);
    btn.appendChild(clipboardIcon());
    const label = h('span', 'copy-label', 'Copy');
    btn.appendChild(label);
    const flash = (): void => {
      const prev = label.textContent ?? 'Copy';
      btn.classList.add('copied');
      label.textContent = 'Copied';
      setTimeout(() => { btn.classList.remove('copied'); label.textContent = prev; }, 1500);
    };
    btn.addEventListener('click', () => {
      void (async () => {
        try {
          await navigator.clipboard.writeText(getText());
          flash();
        } catch {
          if (legacyCopy(getText())) flash(); // non-secure context / blocked async clipboard
        }
      })();
    });
    return btn;
  }

  /** A result section matched to the app's Score Analysis pattern: an uppercase header above a plain
   *  white card holding the content. */
  private section(title: string, fill: (card: HTMLElement) => void): HTMLElement {
    const wrap = h('div', 'bs-section');
    const head = h('div', 'col-head');
    head.appendChild(h('h3', 'col-h', title));
    wrap.appendChild(head);
    // NOTE: not the shared .card class — its --panel background is dark (#232323); the Score Analysis
    // card only looks white because .scroll-box has an .app-region override. Use an explicit white card.
    const card = h('div', 'bs-card-pad');
    fill(card);
    wrap.appendChild(card);
    return wrap;
  }

  /** A horizontal score bar. The "how to read it" text lives in a hover tooltip, not on-screen; the
   *  two extreme edges of the scale are labelled above the track (min left, max right) so the range
   *  is self-explanatory, and the current rung reads from where the fill lands between them. */
  private meter(label: string, value: number, band: BuyerBand, scale: 'belief' | 'commitment' | 'evidence', extraTip?: string): HTMLElement {
    const m = h('div', 'bs-meter');
    const top = h('div', 'bs-meter-top');
    const lwrap = h('span', 'bs-meter-l', label);
    const tipText = extraTip ? `${METER_TIP[scale]} ${extraTip}` : METER_TIP[scale];
    const tip = h('span', 'bs-tip', 'i');
    tip.setAttribute('data-tip', tipText);
    tip.setAttribute('tabindex', '0');
    tip.setAttribute('role', 'note');
    tip.setAttribute('aria-label', tipText);
    lwrap.appendChild(tip);
    top.appendChild(lwrap);
    // Score shown out of 100, e.g. "82 / 100" (the "/ 100" is muted so the figure still leads).
    const val = h('span', 'bs-meter-val', String(value));
    val.appendChild(h('span', 'bs-meter-max', ' / 100'));
    top.appendChild(val);
    m.appendChild(top);
    // Extreme-edge labels above the track: the scale's floor (None) and ceiling (Strong).
    const ends = h('div', 'bs-meter-ends');
    ends.appendChild(h('span', 'bs-end', BAND_WORDS[scale].None));
    ends.appendChild(h('span', 'bs-end', BAND_WORDS[scale].Strong));
    m.appendChild(ends);
    const track = h('div', 'bs-meter-track');
    const fill = h('div', `bs-meter-fill ${BAND_CLASS[band]}`);
    fill.style.width = `${value}%`; // CSSOM, CSP-safe
    track.appendChild(fill);
    m.appendChild(track);
    return m;
  }
}

/** The Evidence tooltip's attribution line. When no statement is confidently buyer-attributed, the
 *  read rests on grounded-but-unclear-speaker signals — say so plainly (it's why Evidence can be
 *  non-zero with 0 buyer statements) rather than the misleading "based on 0 buyer statements". */
function evidenceAttrLine(a: { buyer_statements: number; seller_statements_excluded: number; unattributed: number }): string {
  const nb = a.buyer_statements;
  const nu = a.unattributed;
  const ns = a.seller_statements_excluded;
  const sellerPart = ns ? `, ${ns} seller statement${ns === 1 ? '' : 's'} excluded` : '';
  if (nb > 0) {
    return `Based on ${nb} buyer statement${nb === 1 ? '' : 's'}${sellerPart}. Buyer attribution only; seller words never move the score.`;
  }
  const unattrPart = nu
    ? ` This read rests on ${nu} grounded signal${nu === 1 ? '' : 's'} whose speaker couldn't be confirmed as the buyer, so treat it as low confidence and aim for more buyer talk-time next time.`
    : ' Treat it as low confidence.';
  return `No statements were confidently attributed to the buyer${sellerPart}.${unattrPart} Seller words never move the score.`;
}

function quadLabel(q: BuyerSideResult['quadrant']): string {
  return q === 'advancing' ? 'Advancing' : q === 'warm_stalled' ? 'Warm · Stalled' : q === 'false_positive' ? 'False-Positive · Fragile' : 'At-Risk · Going Dark';
}

/** The four buying-case questions, in display order, paired with their WhySignals key. */
const WHY_QUESTIONS: Array<[keyof WhySignals, string]> = [
  ['why_solution', 'Why they seek a solution'],
  ['why_now', 'Why now'],
  ['why_us', 'Why us'],
  ['cost_of_inaction', 'Cost of inaction (CIA)'],
];

/** Serialize the buying case to clean plain text (question headers + bullet quotes) for the clipboard. */
function whyToText(w: WhySignals): string {
  const lines: string[] = ['Why the buyer is buying'];
  for (const [key, label] of WHY_QUESTIONS) {
    lines.push('', label);
    const quotes = w[key];
    if (quotes.length) for (const q of quotes) lines.push(`- "${q}"`);
    else lines.push('- No evidence found in this call.');
  }
  return lines.join('\n');
}

/** Plain-text render of the Belief & Commitment Quotes, for the section's Copy button. */
function quotesToText(quotes: BuyerSideResult['quotes']): string {
  const lines: string[] = ['Belief & Commitment Quotes'];
  for (const q of quotes) {
    lines.push('', `"${q.quote}" — ${q.label}`);
    if (q.why) lines.push(q.why);
  }
  return lines.join('\n');
}

/** The clipboard glyph used on the app's copy buttons, built via the DOM (no innerHTML — CSP posture). */
function clipboardIcon(): SVGElement {
  const NS = 'http://www.w3.org/2000/svg';
  const svg = document.createElementNS(NS, 'svg');
  svg.setAttribute('viewBox', '0 0 24 24');
  svg.setAttribute('width', '15');
  svg.setAttribute('height', '15');
  svg.setAttribute('fill', 'none');
  svg.setAttribute('stroke', 'currentColor');
  svg.setAttribute('stroke-width', '2');
  svg.setAttribute('stroke-linecap', 'round');
  svg.setAttribute('stroke-linejoin', 'round');
  const rect = document.createElementNS(NS, 'rect');
  const rectAttrs: Array<[string, string]> = [['x', '9'], ['y', '9'], ['width', '11'], ['height', '11'], ['rx', '2']];
  for (const [k, v] of rectAttrs) rect.setAttribute(k, v);
  const path = document.createElementNS(NS, 'path');
  path.setAttribute('d', 'M5 15V5a2 2 0 0 1 2-2h10');
  svg.appendChild(rect);
  svg.appendChild(path);
  return svg;
}

/** Clipboard fallback for non-secure contexts where navigator.clipboard is unavailable. */
function legacyCopy(text: string): boolean {
  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.style.position = 'fixed';
    ta.style.top = '-1000px';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.focus();
    ta.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
}
